/**
 * SKE-4539. Whether the tag block is offered at all.
 *
 * Video used to be excluded outright, which was right while the phone app was
 * the only way Instagram posts published: it cannot place a tag. Accounts on
 * the Meta publishing API can, and Meta accepts user tags on feed video and on
 * both story types, so the exclusion now depends on whether anything selected
 * could actually carry the tags.
 *
 * Deliberately a pure function rather than inline in the form: the form is
 * expensive to render in a test, and this rule decides whether a whole feature
 * is reachable.
 */
export function shouldShowPostTags({
  isVideoType,
  hasVisibleTagField,
  apiCapableAccountCount,
  isStory = false,
}: {
  isVideoType: boolean;
  hasVisibleTagField: boolean;
  apiCapableAccountCount: number;
  /**
   * SKE-4669. A story tag exists only on the Meta publishing API: the
   * phone app has no way to carry one, so a phone-track story publishes with
   * the tags dropped and nothing said.
   *
   * Offering the control anyway meant a permanent yellow capability warning
   * under every phone-track image story, tags set or not, where the composer
   * previously showed no tag control on a story at all. Gating the control on
   * capability puts a phone-only selection back to exactly that, and leaves the
   * warning to the one case it is for: a mixed selection, where some accounts
   * will carry the tags and some will not.
   */
  isStory?: boolean;
}): boolean {
  if (!hasVisibleTagField) return false;
  if (isStory) return apiCapableAccountCount > 0;
  if (!isVideoType) return true;
  return apiCapableAccountCount > 0;
}
